const get = require('lodash/get')
const keyBy = require('lodash/keyBy')

const { getServiceForLocationId } = require('../service/tools')

const tools = require('./tools')
const utils = require('./utils')
const api = require('./api')
const { isPPMV: isPPMVLocation } = require('../location/tools')
const { EntityApi } = require('../common')
const { wrapEntityApi } = require('../utils/wrap-api')
const { locationIdToProperties } = require('../tools')
const { CouchProductToPg } = require('./couch-pg-sync')
const { pgRowsToProducts } = require('./tools/pg-rows-to-products')
const { resolveReadSource } = require('../tools/resolve-read-source')
const { groupIssuesByRule } = require('./tools/import-validation/content/report')

const rawMethods = {
  export: api.exportProducts,
  import: api.importProducts,
  importProductData: api.importProductData,
  commitProducts: api.commitProducts,
  getByIds: api.getByIds,
  save: api.save,
  get: api.get,
  listAll: api.listAll,
  listAllForDates: api.listAllForDates,
  listForLocations: api.listForLocations,
  listForPrograms: api.listProductsForPrograms,
  listForServices: api.listProductsForServices,
  toGenericQuantities: api.toGenericQuantities,
  list: api.list,
  tools,
  utils
}

const ProductDataAdapter = require('./data-access/pouchdb-adapter/product-pouch-adapter')
const ProductRestAdapter = require('./data-access/rest-adapter')
const { ProductPGAdapter } = require('./data-access/product-pg-adapter')
const ProductBasePGAdapter = require('./data-access/product-base-pg-adapter')
const ProductTagAdapter = require('./data-access/product-tag-pg-adapter')
const ProductPresentationPGAdapter = require('./data-access/product-presentation-pg-adapter')
const { ProductSimilarProductsPGAdapter } = require('./data-access/product-similar-products-pg-adapter')
const ProductSKUPGAdapter = require('./data-access/product-sku-pg-adapter')

class ProductApi extends EntityApi {
  constructor (state, logger, agaveAdapter, pgConnection, priceAdapter) {
    const { user, productsDB } = state
    const pouchAdapter = new ProductDataAdapter(user, productsDB, logger)
    // sets this.adapter = adapter
    super(pouchAdapter)
    // TODO: remove this when all raw methods have been ported
    const apiMethods = wrapEntityApi(rawMethods, state)
    Object.assign(this, apiMethods)

    this.user = user
    this.state = state
    this.agaveAdapter = agaveAdapter
    this.restAdapter = new ProductRestAdapter(agaveAdapter, logger)
    this.logger = logger
    if (pgConnection) {
      this.pgAdapter = new ProductPGAdapter(logger, pgConnection, user.name, priceAdapter)
      this.productBase = new ProductBasePGAdapter(logger, pgConnection, user.name)
      this.productPresentation = new ProductPresentationPGAdapter(logger, pgConnection, user.name)
      this.productSimilarProducts = new ProductSimilarProductsPGAdapter(logger, pgConnection, user.name)
      this.productTag = new ProductTagAdapter(logger, pgConnection, user.name)
      this.productSKU = new ProductSKUPGAdapter(logger, pgConnection, user.name)
      this.couchToPg = new CouchProductToPg(pgConnection, this.getByIds, logger)
    }

    // Wrapped rather than declared as a class method because the Object.assign
    // above copies the raw method onto the instance, which would shadow one.
    //
    // After the pg block on purpose: `couchToPg` syncs couch into PG and has to
    // keep reading the pouch whoever it runs as, so it captures the raw method.
    this.getByIdsFromPouch = this.getByIds
    this.getByIds = (productIds, options) => {
      const source = this.readSource({ options })

      if (source === 'pg') {
        return this.getByIdsFromPg(productIds)
      }

      if (source === 'agave') {
        return this.getProductsViaIds(productIds)
      }

      return this.getByIdsFromPouch(productIds, options)
    }
  }

  // Three-way dispatch for `getByIds`: 'pg' inside agave, 'agave' for an
  // online-only browser, 'pouch' for everyone else. Unlike the two-way domains,
  // agave's own `product/by-ids` handler calls `getByIds` rather than the pg
  // adapter directly, so the 'pg' branch is what answers there instead of
  // calling back out to the endpoint it is serving.
  //
  // `options` is `{ date }` or `{ resolveAlias }`; neither the pg read nor the
  // endpoint can honour them, so an options read stays on the pouch.
  readSource ({ options } = {}) {
    return resolveReadSource({
      domain: 'product',
      state: this.state,
      hasPg: !!this.productSKU,
      hasAgaveTransport: !!this.agaveAdapter,
      mustReadLocally: options !== undefined
    })
  }

  async promoted (locationId, { location, products, allocations, withAllocation = true } = {}) {
    location = location || await this.mainApi.location.get(locationId)

    // There is no promotions available for PPMV locations
    if (!products || (withAllocation && !allocations) || isPPMVLocation(location)) {
      return {
        products: {},
        totalProducts: 0
      }
    }

    let filteredProducts = Object.values(products)
    if (withAllocation) {
      const subscriptionIds = get(allocations, locationId, [])
      filteredProducts = Object.values(products).filter(
        p => !subscriptionIds.includes(p._id)
      )
    }

    const promotedProducts = tools.getPromoted(filteredProducts)

    return {
      products: promotedProducts,
      totalProducts: Object.values(promotedProducts).flat().length
    }
  }

  async marketDataRecommendations (locationId, { limit = 10, products, location } = {}) {
    const territory = getServiceForLocationId(locationId)

    location = location || await this.mainApi.location.get(locationId)
    // There is no market data available for PPVM locations
    if (isPPMVLocation(location)) {
      return {
        marketName: territory.name,
        products: {},
        totalProducts: 0
      }
    }

    if (!products) {
      ({ products } = await this.mainApi.product.listForLocations([locationId]))
    }

    if (Array.isArray(products)) {
      products = keyBy(products, '_id')
    }

    let categoryStats = { earnings: [], speed: [], popularity: [] }
    try {
      categoryStats = await this.agaveAdapter.get('stats/all', { market: territory.rdsMarketAlias, limit: limit * 2 })
    } catch (error) {
      console.error(`Error while retrieving all product category stats`, error)
    }
    const recommendations = {}

    // We filter out products with no price but we allow the price
    // to be 0, in case in the future we want to add some products
    // we want to give for free.
    Object.entries(categoryStats).forEach(([category, statCategory]) => {
      statCategory.sort((a, b) => {
        const rankA = Number(a.rank) || Number.MAX_SAFE_INTEGER
        const rankB = Number(b.rank) || Number.MAX_SAFE_INTEGER
        return rankA - rankB
      })
      recommendations[category] = statCategory
        .filter(p => p.sellPrice !== undefined && p.sellPrice !== null)
        .map(p => products[`product:${p.sku}`])
        .filter(p => p)
        .slice(0, limit)
    })

    // Flat takes 1 level deep arrays inside array and treats it as one array
    const totalProducts = Object.values(recommendations).flat().length

    return {
      marketName: territory.name,
      products: recommendations,
      totalProducts: totalProducts
    }
  }

  async marketDataTopProduct (locationId, category, options = {}) {
    const recommendations = await this.marketDataRecommendations(locationId, { limit: 1, ...options })
    return {
      marketName: recommendations.marketName,
      product: get(recommendations, `products.${category}.[0]`)
    }
  }

  async bundleForProducts (products, isPPMVLocation, isGRlocation) {
    const { isCore, isPPMVCore, CORE_BUNDLES } = tools
    if (isPPMVLocation || isGRlocation) {
      return {
        ...CORE_BUNDLES.ppmv,
        products: products.filter(isPPMVCore).map(p => p._id)
      }
    }
    return {
      ...CORE_BUNDLES.default,
      products: products.filter(isCore).map(p => p._id)
    }
  }

  async bundleForLocation (locationId, { location, products } = {}) {
    const { isCore, isPPMVCore, CORE_BUNDLES } = tools
    location = location || await this.mainApi.location.get(locationId)

    if (!products) {
      ({ products } = await this.mainApi.product.listForLocations([locationId]))
    }

    let productsArray = products
    if (!Array.isArray(productsArray)) {
      productsArray = Object.values(productsArray)
    }

    if (isPPMVLocation(location)) {
      return {
        ...CORE_BUNDLES.ppmv,
        products: productsArray.filter(isPPMVCore).map(p => p._id)
      }
    }
    return {
      ...CORE_BUNDLES.default,
      products: productsArray.filter(isCore).map(p => p._id)
    }
  }

  // Gets the upcoming price for all products relevant to the location territory
  async getUpcomingPrices (locationId, { filter = null } = {}) {
    const { service: serviceId } = getServiceForLocationId(locationId)
    const products = await this.mainApi.product.listForServices([serviceId])
    const upcomingPrices = tools.getUpcomingPrices(products, filter)
    const { country, state } = locationIdToProperties(locationId)
    return {
      location: {
        country,
        state
      },
      ...upcomingPrices
    }
  }

  async getUpcomingPricesOnline (locationId, { filter = null } = {}) {
    const { service: serviceId } = getServiceForLocationId(locationId)
    const upcomingPrices = await this.agaveAdapter.get(`price/list/upcoming/${serviceId}`, { productGroup: filter })
    const { country, state } = locationIdToProperties(locationId)
    return {
      location: {
        country,
        state
      },
      ...upcomingPrices
    }
  }

  // Gets the current price for all products relevant to the location territory
  async getCurrentPrices (locationId, { filter = null } = {}) {
    const { service: serviceId } = getServiceForLocationId(locationId)
    const products = await this.mainApi.product.listForServices([serviceId])
    const prices = tools.getCurrentPrices(products, filter)
    const { country, state } = locationIdToProperties(locationId)
    return {
      location: {
        country,
        state
      },
      ...prices
    }
  }

  async getCurrentPricesOnline (locationId, { filter = null } = {}) {
    const { service: serviceId } = getServiceForLocationId(locationId)
    const prices = await this.agaveAdapter.get(`price/list/current/${serviceId}`, { productGroup: filter })
    const { country, state } = locationIdToProperties(locationId)
    return {
      location: {
        country,
        state
      },
      ...prices
    }
  }

  getProductsViaIds (productIds) {
    if (this.agaveAdapter) {
      return this.agaveAdapter.create('product/by-ids', { productIds })
    }
    // Fallback to pouch. The raw read rather than `getByIds`, which resolves
    // its own source and would land back here.
    return this.getByIdsFromPouch(productIds)
  }

  // Uploads a bulk-import sheet to agave for server-side structural validation.
  validateStructure (buffer, options = {}) {
    if (!this.agaveAdapter) {
      throw new Error('validateStructure requires an agave connection')
    }
    const formData = new window.FormData()
    formData.append('file', new window.Blob([buffer]), 'products.xlsx')
    formData.append('options', JSON.stringify(options))
    return this.agaveAdapter.create('product/import/validate/structure', formData)
  }

  // Unlike validateStructure, the response streams one NDJSON
  // line per row as agave validates it, so onProgress(({processed, total}))
  // can be called as each line arrives instead of only once at the end.
  async validateContent (buffer, options = {}, onProgress) {
    if (!this.agaveAdapter) {
      throw new Error('validateContent requires an agave connection')
    }
    const formData = new window.FormData()
    formData.append('file', new window.Blob([buffer]), 'products.xlsx')
    formData.append('options', JSON.stringify(options))

    const response = await this.agaveAdapter.createStream('product/import/validate/content', formData)
    const reader = response.body.getReader()
    const decoder = new window.TextDecoder()
    const issues = []
    let buffered = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffered += decoder.decode(value, { stream: true })
      const lines = buffered.split('\n')
      buffered = lines.pop()

      for (const line of lines) {
        if (!line) continue
        const update = JSON.parse(line)
        if (update.error) throw new Error(update.error)
        issues.push(...(update.issues || []))
        if (onProgress) {
          onProgress({ processed: update.processed, total: update.total })
          await new Promise(resolve => setTimeout(resolve, 0))
        }
      }
    }

    return { issues: groupIssuesByRule(issues) }
  }

  // Runs once structure/content validation both pass with no errors
  validateSummary (buffer, options = {}) {
    if (!this.agaveAdapter) {
      throw new Error('validateSummary requires an agave connection')
    }
    const formData = new window.FormData()
    formData.append('file', new window.Blob([buffer]), 'products.xlsx')
    formData.append('options', JSON.stringify(options))
    return this.agaveAdapter.create('product/import/validate/summary', formData)
  }

  commitCreate (buffer, options = {}) {
    if (!this.agaveAdapter) {
      throw new Error('commitCreate requires an agave connection')
    }
    const formData = new window.FormData()
    formData.append('file', new window.Blob([buffer]), 'products.xlsx')
    formData.append('options', JSON.stringify(options))
    return this.agaveAdapter.create('product/import/commit', formData)
  }

  // The PG side of `product/by-ids`, for callers with no local integrated-data
  // pouch to read. Answers in the same shape as `getByIds` — see
  // `pgRowsToProducts` for what PG cannot supply.
  //
  // Falls back to the pouch read where there is no PG connection at all, so
  // this stays safe to call from anywhere the api is built.
  async getByIdsFromPg (productIds = []) {
    if (!this.productSKU) {
      return this.getByIdsFromPouch(productIds)
    }

    const rows = await this.productSKU.getProductRowsForSkus(productIds)
    return pgRowsToProducts(rows)
  }

  // Getting the list of similar products for a given list of product ids
  // Both parameter are optional. If no productIds are provided, it will return
  // the similar products for all products in the location.
  // LocationFsid is required only if there's no agaveAdapter aka. we have a PG connection
  // We implicitly assume that agaveAdapter and PgConnection are mutually exclusive

  async getSimilarProducts ({ locationFsid: locationFsidParam } = {}) {
    // We always try to get the locationFsid from the user object first
    // then we fallback to the locationFsidParam if it's not available
    const locationFsid = get(this.user, 'location.id') || locationFsidParam
    if (this.agaveAdapter) {
      return this.agaveAdapter.get('product/similar-products', {
        locationFsid
      })
    }
    if (!locationFsid) {
      throw new Error('locationFsid is required to get similar product data')
    }
    const rows = await this.productSimilarProducts.getSimilarProductGroups({
      locationFsid
    })
    return rows.reduce((result, row) => ({
      ...result, [`product:${row.product_id}`]: row.similar_products_list.map(product => `product:${product}`)
    }), {})
  }
}

module.exports = rawMethods
module.exports.ProductApi = ProductApi
